import { jsx } from 'react/jsx-runtime';
import { createContext, useEffect, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getGatewayConfigOverridesFromEnv, getURLParamsGatewayConfigOverrides } from './gateway-config-overrides.js';
import { DEFAULT_GATEWAY_CONFIGS } from './gateway-config-model.js';
import '../authentication/AuthenticationContextProvider.js';
import { useIsAuthenticated, useIsInternalUser } from '../authentication/permissions.js';
import { useLocalStorage } from '../browser-storage/browser-storage.js';
import { logError } from '../errors/error-reporting.js';
import { gql } from 'urql';
import { useQuery } from '../graphql/graphql.js';
import { GetGatewayConfigsContext } from '../generated/graphql.js';

const GET_GATEWAY_CONFIGS = gql`
  query GetGatewayConfigs {
    gatewayConfigs {
      returnedSeriesLimit
      returnedDatapointsLimit
      tracingUIEnabled
      collectionsAccess
      serviceUIEnabled
      sloUIEnabled
      enableExternalServiceConfig
      enableMetricsDDX
      alertEntityUIEnabled
      aiGatewayEnabled
    }
    tenant {
      tenantConfiguration {
        scrapeInterval
      }
    }
  }
`;
const GatewayConfigsContext = /*#__PURE__*/ createContext({
    isLoading: false,
    originalGatewayConfigs: DEFAULT_GATEWAY_CONFIGS,
    gatewayConfigs: DEFAULT_GATEWAY_CONFIGS,
    setGatewayConfigs: ()=>{
    // noop
    },
    reset: ()=>{
    // noop
    }
});
function GatewayConfigsContextProvider(props) {
    const isAuthenticated = useIsAuthenticated();
    const [searchParams] = useSearchParams();
    const [{ data, error, fetching }] = useQuery({
        query: GET_GATEWAY_CONFIGS,
        context: GetGatewayConfigsContext,
        pause: !isAuthenticated
    });
    // Since we "handle" this error instead of throwing it, make sure it gets reported
    useEffect(()=>{
        if (error === undefined || error === null) return;
        logError(error);
    }, [
        error
    ]);
    const configs = data && !error ? {
        ...data?.gatewayConfigs,
        ...data?.tenant?.tenantConfiguration
    } : DEFAULT_GATEWAY_CONFIGS;
    const isChronoAdmin = useIsInternalUser();
    const envConfigLocalOverrides = getGatewayConfigOverridesFromEnv();
    const [localStorageGatewayConfigs, setLocalStorageGatewayConfigs] = useLocalStorage('gatewayConfigsOverrides', {});
    // This hook is responsible for checking if there are any URL params that override the gateway configs
    useEffect(()=>{
        const paramOverrides = getURLParamsGatewayConfigOverrides(searchParams.toString());
        // If there are URL param overrides, we need to update the local storage overrides with them
        if (Object.keys(paramOverrides).length > 0) {
            // Confirm with the user if they have existing local storage overrides
            if (Object.keys(localStorageGatewayConfigs).length > 0) {
                const confirmed = window.confirm('You have opened a link with URL gateway config overrides but you have existing overrides set. Do you want to apply the overrides from the URL? Any existing overrides will be overwritten.');
                if (!confirmed) {
                    return;
                }
            }
            setLocalStorageGatewayConfigs(paramOverrides);
        }
    // Only do this once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Reset the local storage overrides to original state
    const reset = useCallback(()=>{
        setLocalStorageGatewayConfigs({}); // Clear the local storage overrides
    }, [
        setLocalStorageGatewayConfigs
    ]);
    // Only merge the configs if the user is a Chrono Admin
    const mergedConfigs = isChronoAdmin ? {
        ...configs,
        ...envConfigLocalOverrides,
        ...localStorageGatewayConfigs
    } : configs;
    return /*#__PURE__*/ jsx(GatewayConfigsContext.Provider, {
        value: {
            isLoading: fetching,
            originalGatewayConfigs: configs,
            gatewayConfigs: mergedConfigs,
            setGatewayConfigs: setLocalStorageGatewayConfigs,
            reset
        },
        children: props.children
    });
}
function useGatewayConfigsState() {
    const { gatewayConfigs, setGatewayConfigs, originalGatewayConfigs, reset } = useContext(GatewayConfigsContext);
    return {
        originalGatewayConfigs,
        gatewayConfigs,
        setGatewayConfigs,
        reset
    };
}
function useGatewayConfigs() {
    const { gatewayConfigs } = useGatewayConfigsState();
    return gatewayConfigs;
}
function areGatewayConfigsLoading(gatewayConfigs) {
    // Gateway configs will return the default values object until data has
    // been loaded from the server, so wait until we have server data
    return gatewayConfigs === DEFAULT_GATEWAY_CONFIGS;
}

export { GatewayConfigsContext, GatewayConfigsContextProvider, areGatewayConfigsLoading, useGatewayConfigs, useGatewayConfigsState };
//# sourceMappingURL=GatewayConfigs.js.map
